import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
	ReflectionService,
	ReflectionServiceViewResponse,
	SKILL_BUILDER_TYPES,
} from '@services/member';
import {
	DomainService,
	LanguageService,
	StorageService,
	TEMP_KEY_PREFIX,
	TempKey,
	VolatileKey,
} from '@services/public';
import { domainUID, skillUID } from 'jakapa-utilities-lite';
import { Subscription } from 'rxjs';
import {
	ReflectionPage,
	REFLECTION_PREVIOUS_PAGE,
	ReflectionData,
} from './common';

const PAGE_PATH: string = 'skillBuilders.reflection.pages.main';

@Component({
	selector: 'app-member-skill-builder-reflection',
	templateUrl: './reflection.component.html',
	styleUrls: ['./reflection.component.scss'],
})
export class MemberSkillBuilderReflectionComponent
	implements OnInit, OnDestroy
{
	ReflectionPage: typeof ReflectionPage = ReflectionPage;
	data: ReflectionData;
	private _skillUIDs: Array<skillUID>;
	private _subscriptions: Subscription = new Subscription();

	// Page langauge.
	page: { [key: string]: string } = {
		title: '',
	};

	constructor(
		private _domainSvc: DomainService,
		private _languageSvc: LanguageService,
		private _reflectionSvc: ReflectionService,
		private _router: Router,
		private _storageSvc: StorageService,
	) {}

	async ngOnInit(): Promise<void> {
		// Get page language.
		this._languageSvc.get([PAGE_PATH]).then((value) => {
			if (typeof value[PAGE_PATH] === 'object' && value[PAGE_PATH] !== null)
				this.page = value[PAGE_PATH];
		});

		// Get reflection data.
		this.data = this._storageSvc.getTempStorage(TempKey.REFLECTION);
		const domainUID: domainUID = this._storageSvc.getVolatileStorage(
			VolatileKey.DOMAIN_UID,
			true,
		);
		const cycle: number = this._storageSvc.getVolatileStorage(
			VolatileKey.CYCLE,
			true,
		);
		if (!this.data) {
			// Redirect to the dashboard if domain UID or cycle is not set.
			if (typeof domainUID !== 'number' || typeof cycle !== 'number') {
				this._router.navigate(['member/dashboard']);
				return;
			}

			// Retrieve the skills for this domain and cycle.
			const path = `domains2.${domainUID}.reflection.cycles.${cycle}.skills`;
			await this._languageSvc.get([path]).then((value) => {
				if (typeof value[path] === 'object' && value[path] !== null)
					this._skillUIDs = this._languageSvc.objectToOrderedArray(value[path]);
				else
					this._skillUIDs = this._domainSvc
						.getSkills(domainUID)
						.map((skill) => skill.uid);
			});
			this._skillUIDs.sort((a, b) => {
				if (this._domainSvc.getSkill(a).name < this._domainSvc.getSkill(b).name)
					return -1;
				if (this._domainSvc.getSkill(a).name > this._domainSvc.getSkill(b).name)
					return 1;
				return 0;
			});

			// Retrieve skill scores.
			this._reflectionSvc
				.view(domainUID)
				.subscribe((res: ReflectionServiceViewResponse) => {
					this.data = {
						type: SKILL_BUILDER_TYPES.REFLECTION,
						start: true,
						page: ReflectionPage.SCORES,
						domainUID: domainUID,
						cycle: cycle,
						scores: this._skillUIDs.map((skillUID) => {
							const self = res.self.find((score) => score.uid === skillUID);
							const peer = res.peer.find((score) => score.uid === skillUID);
							return {
								uid: skillUID,
								self: self ? Math.round(self.score * 10) / 10 : null,
								peer: peer ? Math.round(peer.score * 10) / 10 : null,
							};
						}),
					};
					this._setStorageSubscription();
				});
		} else this._setStorageSubscription();
	}

	private _setStorageSubscription() {
		this._subscriptions.add(
			this._storageSvc.updates.subscribe((update) => {
				if (update.key === TEMP_KEY_PREFIX + TempKey.ALL)
					this.data = update.value[TempKey.REFLECTION];
				if (update.key === TEMP_KEY_PREFIX + TempKey.CLEAR) this.data = null;
				if (update.key === TEMP_KEY_PREFIX + TempKey.REFLECTION)
					this.data = update.value;
				if (!this.data && !this._router.getCurrentNavigation())
					this._router.navigate(['member/dashboard']);
			}),
		);
	}

	back() {
		let page: ReflectionPage = REFLECTION_PREVIOUS_PAGE[this.data.page];
		if (!page) {
			this._router.navigate(['member/dashboard']);
			return;
		}
		this._storageSvc.setTempStorage(TempKey.REFLECTION, {
			...this.data,
			page,
		});
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
		this._storageSvc.setTempStorage(TempKey.REFLECTION, null);
	}
}
